import React, { useState, useEffect, useRef } from 'react';
import { 
    Table, 
    Pagination, 
    Spinner, 
    Alert, 
    Container, 
    Form, 
    Button,
    OverlayTrigger,
    Tooltip,
    Card,
    Row,
    Col,
    Badge,
    InputGroup
} from 'react-bootstrap';
import { 
    BiError,
    BiVideo
} from 'react-icons/bi';
import { BsPlayCircle, BsCloudUpload } from 'react-icons/bs';
import {
    BsCheckCircleFill,
    BsPersonSlash,
    BsXCircleFill
} from 'react-icons/bs';
import { FaUserAlt, FaUserClock, FaUserSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../api';
import { useParams } from 'react-router-dom';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import useBancaTecnicoStore from '../../../stores/bancaTecnicoStore';
import VideoUploadModal from './VideoUploadModal';
import VideoViewModal from './VideoViewModal';
import HelpDrawerVideos from './HelpDrawerVideos';
import { BsQuestionCircle } from 'react-icons/bs';
import './CandidatosBancaVideo.css';

interface CandidatoEdital {
    id: string;
    inscricao: string;
    candidatoId: string;
    editalId: string;
    nomeEdital: string;
}

interface CandidatoBancaResponse {
    candidatoId: string;
    nome: string;
    nomeSocial: string;
    candidatoEditais: CandidatoEdital[];
    statusCandidato: StatusCandidato;
    resultado: ResultadoCandidato;
    file_name: string | null;
    fileName?: string | null;
    emailResultadoConfirmado: boolean | null;
    emailResultadoEnviado: boolean | null;
    hasVideo: boolean;
}

interface Estatisticas {
    total: number;
    validados: number;
    naoValidados: number;
    ausentes: number;
    inconclusivos: number;
    indefinidos: number;
}

interface EstatisticasVideo {
    totalComVideo: number;
    totalSemVideo: number;
}

interface CandidatosComEstatisticasResponse {
    candidatos: {
        content: CandidatoBancaResponse[];
        totalElements: number;
        totalPages: number;
        first: boolean;
        last: boolean;
        numberOfElements: number;
    };
    estatisticas: Estatisticas;
    estatisticasVideo: EstatisticasVideo;
}

enum StatusCandidato {
    NAO_AVALIADO = "NAO_AVALIADO",
    INCONCLUSIVO = "INCONCLUSIVO",
    VALIDADO = "VALIDADO",
    NAO_VALIDADO = "NAO_VALIDADO",
    DELETADO = "DELETADO",
    AUSENTE = "AUSENTE"
}

enum ResultadoCandidato {
    VALIDADO = "VALIDADO",
    NAO_VALIDADO = "NAO_VALIDADO",
    AUSENTE = "AUSENTE"
}

const StatusDisplayNames: Record<StatusCandidato, string> = {
    [StatusCandidato.NAO_AVALIADO]: "Não avaliado",
    [StatusCandidato.INCONCLUSIVO]: "Inconclusivo",
    [StatusCandidato.VALIDADO]: "Validado",
    [StatusCandidato.NAO_VALIDADO]: "Não validado",
    [StatusCandidato.DELETADO]: "Deletado",
    [StatusCandidato.AUSENTE]: "Ausente"
};

const ResultadoDisplayNames: Record<ResultadoCandidato, string> = {
    [ResultadoCandidato.VALIDADO]: "Validado",
    [ResultadoCandidato.NAO_VALIDADO]: "Não validado",
    [ResultadoCandidato.AUSENTE]: "Ausente"
};

const CandidatosBancaVideo = () => {
    const { processoId, bancaId } = useParams<{ processoId: string, bancaId: string }>();    
    const [candidatos, setCandidatos] = useState<CandidatoBancaResponse[]>([]);
    
    const renderInscricoes = (candidatoEditais: CandidatoEdital[]) => {
        if (!candidatoEditais || candidatoEditais.length === 0) {
            return '-';
        }
        if (candidatoEditais.length === 1) {
            return (
                <div>
                    {candidatoEditais[0].inscricao} - {candidatoEditais[0].nomeEdital}
                </div>
            );
        }
        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip id={`tooltip-${candidatoEditais[0].candidatoId}`}>
                        {candidatoEditais.map((edital: CandidatoEdital, idx: number) => (
                            <div key={edital.id}>
                                {edital.inscricao} - {edital.nomeEdital}
                                {idx < candidatoEditais.length - 1 && <br />}
                            </div>
                        ))}
                    </Tooltip>
                }
            >
                <span className="badge bg-secondary cursor-pointer">{candidatoEditais.length} inscrições</span>
            </OverlayTrigger>
        );
    };
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null);
    const [estatisticasVideo, setEstatisticasVideo] = useState<EstatisticasVideo | null>(null);
    const [filtroNome, setFiltroNome] = useState('');
    const [filtroInscricao, setFiltroInscricao] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedCandidato, setSelectedCandidato] = useState<CandidatoBancaResponse | null>(null);
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [showHelpDrawer, setShowHelpDrawer] = useState(false);
    const [debouncing, setDebouncing] = useState(false);
    const [customFileName, setCustomFileName] = useState<string>('');
    const [loadingSuggestion, setLoadingSuggestion] = useState(false);


    const fetchCandidatos = async () => {
        try {
            setLoading(true);
            const params: any = {
                page,
                size,
                votacao: false,
                sort: 'file_name,asc' // Prioriza candidatos sem vídeo
            };
            if (debouncedFiltros.nome) params.nome = debouncedFiltros.nome;
            if (debouncedFiltros.inscricao) params.inscricao = debouncedFiltros.inscricao;
            if (filtroStatus) params.status = filtroStatus;
            
            const response = await api.get<CandidatosComEstatisticasResponse>(`/bancas/${bancaId}/candidatos/estatisticas`, {
                params
            });
            const candidatosComVideo = response.data.candidatos.content.map((candidato: any) => ({
                ...candidato,
                // aceita tanto file_name quanto fileName vindo do backend
                hasVideo: !!(candidato.file_name || candidato.fileName)
            }));
            setCandidatos(candidatosComVideo);
            setTotalPages(response.data.candidatos.totalPages);
            setTotalElements(response.data.candidatos.totalElements);
            setEstatisticas(response.data.estatisticas);
            setEstatisticasVideo(response.data.estatisticasVideo);
            setError(null);
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Erro ao carregar candidatos';
            setError(errorMsg);
            setCandidatos([]);
            setEstatisticas(null);
            setEstatisticasVideo(null);
            toast.error(BackendErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    // Debounce para filtros de texto
    const debounceTimer = useRef<NodeJS.Timeout>();
    const [debouncedFiltros, setDebouncedFiltros] = useState({
        nome: '',
        inscricao: ''
    });
    
    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        
        if (filtroNome || filtroInscricao) {
            setDebouncing(true);
        }
        
        debounceTimer.current = setTimeout(() => {
            setDebouncedFiltros({
                nome: filtroNome,
                inscricao: filtroInscricao
            });
            setPage(0);
            setDebouncing(false);
        }, 1500);
        
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [filtroNome, filtroInscricao]);
    
    // Status aplica imediatamente
    useEffect(() => {
        setPage(0);
    }, [filtroStatus]);

    useEffect(() => {
        if (bancaId) {
            fetchCandidatos();
        }
    }, [bancaId, page, size, debouncedFiltros.nome, debouncedFiltros.inscricao, filtroStatus]);

    const handleFiltroChange = (tipo: 'nome' | 'inscricao' | 'status', valor: string) => {
        switch (tipo) {
            case 'nome':
                setFiltroNome(valor);
                break;
            case 'inscricao':
                setFiltroInscricao(valor);
                break;
            case 'status':
                setFiltroStatus(valor);
                break;
        }
    };

    const limparFiltros = () => {
        setFiltroNome('');
        setFiltroInscricao('');
        setFiltroStatus('');
        setPage(0);
    };

    const candidatosSemVideoElegivel = estatisticas ? 
        estatisticas.total - estatisticas.ausentes - (estatisticasVideo?.totalComVideo || 0) : 0;

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSize(Number(e.target.value));
        setPage(0);
    };

    const handleShowUploadModal = (candidato: CandidatoBancaResponse) => {
        setSelectedCandidato(candidato);
        setShowUploadModal(true);
    };

    const handleCloseUploadModal = () => {
        setShowUploadModal(false);
        setVideoFile(null);
        setCustomFileName('');
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setVideoFile(file);
            
            if (selectedCandidato) {
                setLoadingSuggestion(true);
                try {
                    const response = await api.get(
                        `/bancas/${bancaId}/candidatos/${selectedCandidato.candidatoId}/video/suggested-name`,
                        { params: { originalFileName: file.name } }
                    );
                    setCustomFileName(response.data);
                } catch (err) {
                    console.error('Erro ao obter nome sugerido:', err);
                    setCustomFileName(file.name);
                } finally {
                    setLoadingSuggestion(false);
                }
            }
        }
    };

    const { decrementVideoUploadRequests } = useBancaTecnicoStore();

    const handleUploadVideo = async () => {
        if (!selectedCandidato || !videoFile) {
            toast.warning('Selecione um arquivo de vídeo');
            return;
        }

        if (!customFileName.trim()) {
            toast.warning('O nome do arquivo não pode estar vazio');
            return;
        }

        if (customFileName.length > 255) {
            toast.error('O nome do arquivo excede o limite de 255 caracteres');
            return;
        }

        const candidatoJaTinhaVideo = selectedCandidato.hasVideo;

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('file', videoFile);
            formData.append('customFileName', customFileName);

            await api.post(
                `/bancas/${bancaId}/candidatos/${selectedCandidato.candidatoId}/video`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            toast.success('Vídeo enviado com sucesso');
            
            // Só decrementa se o candidato não tinha vídeo antes
            if (!candidatoJaTinhaVideo) {
                decrementVideoUploadRequests();
            }
            
            fetchCandidatos();
            handleCloseUploadModal();
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Erro ao enviar vídeo';
            toast.error(BackendErrorMessage(err));
        } finally {
            setUploading(false);
        }
    };

    const handleViewVideo = async (candidato: CandidatoBancaResponse) => {
        try {
            // aceita tanto file_name quanto fileName
            if (!(candidato.file_name || candidato.fileName)) {
                toast.warning('Este candidato nao possui video');
                return;
            }

            setSelectedCandidato(candidato);

            // Usa URL assinada para evitar downloads grandes via API (413 em producao)
            const response = await api.get(`/bancas/${bancaId}/candidatos/${candidato.candidatoId}/videoUrl`);
            if (response.data.url) {
                setVideoUrl(response.data.url);
                setShowVideoModal(true);
                return;
            }

            toast.error('Nao foi possivel obter a URL do video');
        } catch (err: any) {
            const status = err.response?.status;
            const errorMsg = err.response?.data?.message 
                || (status === 413 ? 'Video muito grande para baixar pela API' : 'Erro ao carregar video');
            toast.error(errorMsg);
        }
    };

    const handleCloseVideoModal = () => {
        setShowVideoModal(false);
        if (videoUrl) {
            URL.revokeObjectURL(videoUrl);
            setVideoUrl(null);
        }
    };

    const getRowClass = (candidato: CandidatoBancaResponse): string => {
        const isElegivel = candidato.statusCandidato !== StatusCandidato.AUSENTE && candidato.resultado !== ResultadoCandidato.AUSENTE;
        
        if (!isElegivel) {
            return 'table-light';
        }
        
        if (candidato.hasVideo) {
            return '';
        } else {
            return 'table-danger';
        }
    };

    const getResultadoIcon = (resultado?: ResultadoCandidato): JSX.Element => {
        switch (resultado) {
            case ResultadoCandidato.VALIDADO:
                return <BsCheckCircleFill color="#28a745" size={23} />;
            case ResultadoCandidato.NAO_VALIDADO:
                return <BsXCircleFill color="#dc3545" size={23} />;
            case ResultadoCandidato.AUSENTE:
                return <FaUserSlash color="#6c757d" size={23} />;
            default:
                return <BiError color="#ffc107" size={23} />;
        }
    };

    const getStatusIcon = (status?: StatusCandidato): JSX.Element => {
        switch (status) {
            case StatusCandidato.VALIDADO:
                return <BsCheckCircleFill color="#28a745" size={23} />;
            case StatusCandidato.NAO_VALIDADO:
                return <BsXCircleFill color="#dc3545" size={23} />;
            case StatusCandidato.NAO_AVALIADO:
                return <FaUserAlt color="#17a2b8" size={23} />;
            case StatusCandidato.INCONCLUSIVO:
                return <FaUserClock color="#ffc107" size={23} />;
            case StatusCandidato.AUSENTE:
                return <FaUserSlash color="#6c757d" size={23} />;
            case StatusCandidato.DELETADO:
                return <BsPersonSlash color="#6f42c1" size={23} />;
            default:
                return <BiError color="#ffc107" size={23} />;
        }
    };

    const getResultadoTooltip = (resultado?: ResultadoCandidato): JSX.Element => {
        const displayName = resultado ? ResultadoDisplayNames[resultado] : 'Desconhecido';
        return (
            <Tooltip id="tooltip-resultado">
                <span>{displayName}</span>
            </Tooltip>
        );
    };

    const getStatusTooltip = (status?: StatusCandidato): JSX.Element => {
        const displayName = status ? StatusDisplayNames[status] : 'Desconhecido';
        return (
            <Tooltip id="tooltip-status">
                <span>{displayName}</span>
            </Tooltip>
        );
    };



    const renderVideoActions = (candidato: CandidatoBancaResponse) => {
        // Don't show video actions for absent candidates
        if (candidato.statusCandidato === StatusCandidato.AUSENTE || 
            candidato.resultado === ResultadoCandidato.AUSENTE) {
            return '-';
        }

        return (
            <div className="d-flex gap-1 justify-content-center">
                <OverlayTrigger placement="top" overlay={<Tooltip>Visualizar vídeo</Tooltip>}>
                    <Button
                        variant={candidato.hasVideo ? "dark" : "outline-danger"}
                        size="sm"
                        onClick={() => handleViewVideo(candidato)}
                        disabled={!candidato.hasVideo}
                        className="d-flex align-items-center"
                    >
                        <BsPlayCircle size={16} />
                    </Button>
                </OverlayTrigger>
                
                <OverlayTrigger placement="top" overlay={<Tooltip>Upload de vídeo</Tooltip>}>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleShowUploadModal(candidato)}
                        className="d-flex align-items-center"
                    >
                        <BsCloudUpload size={16} />
                    </Button>
                </OverlayTrigger>
            </div>
        );
    };

    const renderResultado = (candidato: CandidatoBancaResponse) => {
        if (candidato.resultado) {
            return (
                <OverlayTrigger placement="top" overlay={getResultadoTooltip(candidato.resultado)}>
                    <div className="d-inline-flex align-items-center justify-content-center">
                        {getResultadoIcon(candidato.resultado)}
                    </div>
                </OverlayTrigger>
            );
        }
        return (
            <OverlayTrigger placement="top" overlay={getResultadoTooltip(undefined)}>
                <div className="d-inline-flex align-items-center justify-content-center">
                    <BiError size={23} />
                </div>
            </OverlayTrigger>
        );
    };



    return (
        <HelpDrawerVideos isOpen={showHelpDrawer} onClose={() => setShowHelpDrawer(false)}>
        <Container fluid className="py-4">
            {/* Header */}
            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body className="p-4">
                    <Row className="align-items-center mb-3">
                        <Col>
                            <h2 className="mb-1 fw-bold">
                                <BiVideo className="me-2" />
                                Vídeos dos Candidatos
                            </h2>
                            <p className="text-muted mb-0">Gerencie os vídeos da banca</p>
                        </Col>
                        <Col xs="auto">
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setShowHelpDrawer(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
                            >
                                <BsQuestionCircle /> Ajuda
                            </button>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="d-flex gap-3 flex-wrap">
                                <div className="px-3 py-2 bg-light rounded">
                                    <small className="text-muted d-block">Total</small>
                                    <strong className="fs-5">{totalElements}</strong>
                                </div>
                                <div className={`px-3 py-2 rounded ${candidatosSemVideoElegivel === 0 ? 'bg-success-subtle' : 'bg-danger-subtle'}`}>
                                    <small className="text-muted d-block">Faltam Upload</small>
                                    <strong className="fs-5 text-danger">{candidatosSemVideoElegivel}</strong>
                                </div>
                                <div className="px-3 py-2 bg-success-subtle rounded">
                                    <small className="text-muted d-block">Com Vídeo</small>
                                    <strong className="fs-5 text-success">{estatisticasVideo?.totalComVideo || 0}</strong>
                                </div>
                                {estatisticas && (
                                    <>
                                        <div className="px-3 py-2 bg-light rounded">
                                            <small className="text-muted d-block">Validados</small>
                                            <strong className="fs-6 text-success">{estatisticas.validados}</strong>
                                        </div>
                                        <div className="px-3 py-2 bg-light rounded">
                                            <small className="text-muted d-block">Não Validados</small>
                                            <strong className="fs-6 text-danger">{estatisticas.naoValidados}</strong>
                                        </div>
                                        <div className="px-3 py-2 bg-light rounded">
                                            <small className="text-muted d-block">Ausentes</small>
                                            <strong className="fs-6 text-secondary">{estatisticas.ausentes}</strong>
                                        </div>
                                    </>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>



            {/* Filtros */}
            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body className="p-4">
                    <Row className="g-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Filtrar por Nome</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        placeholder="Digite o nome..."
                                        value={filtroNome}
                                        onChange={(e) => handleFiltroChange('nome', e.target.value)}
                                    />
                                    {debouncing && filtroNome && (
                                        <InputGroup.Text>
                                            <Spinner size="sm" />
                                        </InputGroup.Text>
                                    )}
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Filtrar por Inscrição</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        placeholder="Digite a inscrição..."
                                        value={filtroInscricao}
                                        onChange={(e) => handleFiltroChange('inscricao', e.target.value)}
                                    />
                                    {debouncing && filtroInscricao && (
                                        <InputGroup.Text>
                                            <Spinner size="sm" />
                                        </InputGroup.Text>
                                    )}
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Filtrar por Status</Form.Label>
                                <Form.Select
                                    value={filtroStatus}
                                    onChange={(e) => handleFiltroChange('status', e.target.value)}
                                >
                                    <option value="">Todos os status</option>
                                    <option value="VALIDADO">Validado</option>
                                    <option value="NAO_VALIDADO">Não Validado</option>
                                    <option value="AUSENTE">Ausente</option>
                                    <option value="INCONCLUSIVO">Inconclusivo</option>
                                    <option value="NAO_AVALIADO">Não avaliado</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2} className="d-flex align-items-end">
                            <Button variant="outline-secondary" onClick={limparFiltros} className="w-100">
                                Limpar Filtros
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && <Alert variant="danger" className="border-0 shadow-sm">{error}</Alert>}

            {loading ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" role="status" variant="primary">
                            <span className="visually-hidden">Carregando...</span>
                        </Spinner>
                        <p className="mt-3 text-muted">Carregando candidatos...</p>
                    </Card.Body>
                </Card>
            ) : (
                <>
                    <Card className="border-0 shadow-sm">
                        <Table hover responsive className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="border-0">Nome</th>
                                    <th className="border-0">Nome Social</th>
                                    <th className="border-0">Inscrição</th>
                                    <th className="border-0 text-center">Status</th>
                                    <th className="border-0 text-center">Resultado</th>
                                    <th className="border-0 text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidatos.length > 0 ? (
                                    candidatos.map((candidato) => {
                                        return (
                                        <tr key={candidato.candidatoId} className={getRowClass(candidato)}>
                                            <td className="align-middle">{candidato.nome}</td>
                                            <td className="align-middle">{candidato.nomeSocial || '-'}</td>
                                            <td className="align-middle">{renderInscricoes(candidato.candidatoEditais)}</td>
                                            <td className="align-middle text-center">
                                                <OverlayTrigger placement="top" overlay={getStatusTooltip(candidato.statusCandidato)}>
                                                    <div className="d-inline-flex align-items-center justify-content-center">
                                                        {getStatusIcon(candidato.statusCandidato)}
                                                    </div>
                                                </OverlayTrigger>
                                            </td>
                                            <td className="align-middle text-center">
                                                {renderResultado(candidato)}
                                            </td>
                                            <td className="align-middle text-center">
                                                {renderVideoActions(candidato)}
                                            </td>
                                        </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="text-center py-4 text-muted">
                                            Nenhum candidato encontrado
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Card>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="d-flex align-items-center gap-3">
                            <Form.Group className="mb-0">
                                <Form.Label className="me-2 mb-0">Itens por página:</Form.Label>
                                <Form.Select value={size} onChange={handleSizeChange} style={{width: 'auto', display: 'inline-block'}}>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </Form.Select>
                            </Form.Group>
                            <small className="text-muted">
                                Mostrando {candidatos.length} de {totalElements} candidatos
                            </small>
                        </div>
                        
                        {totalPages > 1 && (
                            <Pagination className="mb-0">
                                <Pagination.First onClick={() => handlePageChange(0)} disabled={page === 0} />
                                <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 0} />
                                
                                {[...Array(totalPages).keys()].map((pageNumber) => (
                                    <Pagination.Item
                                        key={pageNumber}
                                        active={pageNumber === page}
                                        onClick={() => handlePageChange(pageNumber)}
                                    >
                                        {pageNumber + 1}
                                    </Pagination.Item>
                                ))}
                                
                                <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalPages - 1} />
                                <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={page === totalPages - 1} />
                            </Pagination>
                        )}
                    </div>
                </>
            )}

            <VideoUploadModal
                show={showUploadModal}
                onHide={handleCloseUploadModal}
                selectedCandidato={selectedCandidato}
                videoFile={videoFile}
                customFileName={customFileName}
                uploading={uploading}
                loadingSuggestion={loadingSuggestion}
                onFileChange={handleFileChange}
                onFileNameChange={setCustomFileName}
                onUpload={handleUploadVideo}
            />

            <VideoViewModal
                show={showVideoModal}
                onHide={handleCloseVideoModal}
                selectedCandidato={selectedCandidato}
                videoUrl={videoUrl}
            />
        </Container>
        </HelpDrawerVideos>
    );
};

export default CandidatosBancaVideo;